import { BRAND } from "../risikoanalyseStep/tokens";

/**
 * Inline-SVG hero illustrations for the Kennzahlen step form.
 * One per "kind" of sub-step.
 */

export type KennzahlenHeroKind =
  | "period"
  | "basis"
  | "risiko"
  | "mitarbeiter"
  | "summary";

const HERO_SIZE = { w: 130, h: 90 };

const Period = () => (
  <svg width={HERO_SIZE.w} height={HERO_SIZE.h} viewBox="0 0 140 96" fill="none">
    <circle cx="70" cy="48" r="36" stroke={BRAND.purple} strokeWidth="2.5" />
    <path
      d="M70 22v26l16 10"
      stroke={BRAND.gold}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <text
      x="14"
      y="22"
      fontFamily={BRAND.fontAccent}
      fontSize="13"
      fill={BRAND.muted}
    >
      2023
    </text>
    <text
      x="106"
      y="22"
      fontFamily={BRAND.fontAccent}
      fontSize="13"
      fill={BRAND.muted}
    >
      2026
    </text>
    <line
      x1="14"
      y1="80"
      x2="126"
      y2="80"
      stroke={BRAND.borderDashed}
      strokeWidth="2"
      strokeDasharray="4 4"
    />
  </svg>
);

const Basis = () => (
  <svg width={HERO_SIZE.w} height={HERO_SIZE.h} viewBox="0 0 140 96" fill="none">
    <line
      x1="14"
      y1="80"
      x2="126"
      y2="80"
      stroke={BRAND.muted}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <rect x="22" y="58" width="14" height="22" rx="2" fill={BRAND.purple} />
    <rect x="44" y="42" width="14" height="38" rx="2" fill={BRAND.gold} />
    <rect x="66" y="50" width="14" height="30" rx="2" fill={BRAND.purple} />
    <rect x="88" y="28" width="14" height="52" rx="2" fill={BRAND.gold} />
    <path
      d="M22 60 L44 44 L66 52 L88 30 L110 24"
      stroke={BRAND.ink}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <circle cx="110" cy="24" r="3" fill={BRAND.ink} />
  </svg>
);

const Risiko = () => (
  <svg width={HERO_SIZE.w} height={HERO_SIZE.h} viewBox="0 0 140 96" fill="none">
    <path
      d="M70 12l34 12v22c0 18-14 32-34 38-20-6-34-20-34-38V24l34-12z"
      fill={BRAND.purple}
    />
    <text
      x="70"
      y="62"
      fontFamily={BRAND.fontAccent}
      fontSize="34"
      fontWeight="600"
      fill={BRAND.gold}
      textAnchor="middle"
    >
      €
    </text>
  </svg>
);

const Mitarbeiter = () => (
  <svg width={HERO_SIZE.w} height={HERO_SIZE.h} viewBox="0 0 140 96" fill="none">
    {[0, 1, 2].map((row) =>
      [0, 1, 2, 3].map((col) => {
        const cx = 22 + col * 30;
        const cy = 24 + row * 24;
        const isAccent = (row + col) % 3 === 0;
        return (
          <g key={`${row}-${col}`}>
            <circle
              cx={cx}
              cy={cy}
              r="6"
              fill={isAccent ? BRAND.gold : BRAND.purple}
            />
            <path
              d={`M${cx - 8} ${cy + 14} q8 -8 16 0`}
              stroke={isAccent ? BRAND.gold : BRAND.purple}
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
            />
          </g>
        );
      })
    )}
  </svg>
);

const Summary = () => (
  <svg width={HERO_SIZE.w} height={HERO_SIZE.h} viewBox="0 0 140 96" fill="none">
    <rect
      x="20"
      y="14"
      width="100"
      height="72"
      rx="8"
      fill={BRAND.white}
      stroke={BRAND.purple}
      strokeWidth="2"
    />
    <line x1="34" y1="32" x2="86" y2="32" stroke={BRAND.muted} strokeWidth="2" strokeLinecap="round" />
    <line x1="34" y1="46" x2="100" y2="46" stroke={BRAND.muted} strokeWidth="2" strokeLinecap="round" />
    <line x1="34" y1="60" x2="76" y2="60" stroke={BRAND.muted} strokeWidth="2" strokeLinecap="round" />
    <circle cx="106" cy="68" r="14" fill={BRAND.gold} />
    <path
      d="M99 68 l5 5 l8 -10"
      stroke={BRAND.ink}
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const HEROES: Record<KennzahlenHeroKind, () => JSX.Element> = {
  period: Period,
  basis: Basis,
  risiko: Risiko,
  mitarbeiter: Mitarbeiter,
  summary: Summary,
};

const StepHero = ({ kind }: { kind: KennzahlenHeroKind }) => {
  const Hero = HEROES[kind];
  return <Hero />;
};

export default StepHero;
